import { ExtendedShareholderDTO } from "../../../types/Shareholder";
import Big from "big.js";
import {
  GetDocumentSigningStatusResponse,
  SigningStatusForSigner,
} from "../../../document/documentApi";
import { ViewDocumentSigningStatusDialogModel } from "../../../dbot/summary/summaryDomain";
import { makeAutoObservable } from "mobx";

import { TMessage } from "../../../i18n/tt";
import { SigningMethod } from "../../../common/common";
import { NumberFieldModel } from "../../../common/commonForms";
import { ShareTypeCode } from "../../../common/share";

export type SignSubscriptionForm = {
  authorName: string;
  companyName: string;
  organisationNumber: string;
  signerName: string;
  documentContentBase64: string;
  signingMethod: SigningMethod;
  isSigned: boolean;
  isShareIssue: boolean;
  subscriptionPeriod: SignSubscriptionPeriod;
  isPreferentialRights: boolean;
  isOversubscriptionAvailable: boolean;
  shareholder?: ExtendedShareholderDTO;
  subscriber: SignSubscriber;
  shares: SignSubscriptionFormShare[];
  signerStatus: SigningStatusForSigner[];
};

export type SignSubscriber = {
  name: string;
  organisationNumber?: string;
};

export type SignSubscriptionPeriod = {
  from?: string;
  to?: string;
  hasStarted: boolean;
  hasEnded: boolean;
};

export type SignSubscriptionFormShare = {
  sharetypeId: number;
  sharetypeCode: ShareTypeCode;
  sharePrice: number;
  availableShares: number;
  numberOfShares: number;
};

export type GetSubscriptionFormSigningStatusResponse = {
  signingStatus: GetDocumentSigningStatusResponse;
  shares: Array<SignSubscriptionFormShare>;
};

export type SubscriptionFormDialogState =
  | { type: "none" }
  | { type: "viewSigningStatus"; model: ViewDocumentSigningStatusDialogModel };

export type SubscriptionModelParams = {
  signerName: string;
  isShareIssue: boolean;
  isPreferentialRights: boolean;
  isOversubscriptionAvailable: boolean;
  subscriptionPeriod: SignSubscriptionPeriod;
  subscriber: SignSubscriber;
  shares: SignSubscriptionFormShare[];
  signerStatus?: SigningStatusForSigner[];
  shareholder?: ExtendedShareholderDTO;
};

export class SubscriptionModel {
  shareholder?: ExtendedShareholderDTO;
  signerName: string;
  isShareIssue: boolean;
  isPreferentialRights: boolean;
  isOversubscriptionAvailable: boolean;
  subscriptionPeriod: SignSubscriptionPeriod;
  subscriber: SignSubscriber;
  shares: SubscriptionShareModel[];
  signers: SigningStatusForSigner[];

  constructor(data: SubscriptionModelParams) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.shareholder = data.shareholder;
    this.signerName = data.signerName;
    this.isShareIssue = data.isShareIssue;
    this.isPreferentialRights = data.isPreferentialRights;
    this.isOversubscriptionAvailable = data.isOversubscriptionAvailable;
    this.subscriptionPeriod = data.subscriptionPeriod;
    this.subscriber = data.subscriber;
    this.shares = this.toSubscriptionShareModel(data.shares ?? []);
    this.signers = data.signerStatus ?? [];
  }

  toSubscriptionShareModel(shares: SignSubscriptionFormShare[]) {
    return shares.map(
      (share) =>
        new SubscriptionShareModel(
          share,
          this.isPreferentialRights && this.isOversubscriptionAvailable,
        ),
    );
  }

  setSigners(signers: SigningStatusForSigner[]) {
    this.signers = signers;
  }

  setShares(shares: SignSubscriptionFormShare[]) {
    if (this.isSubscriptionSigned) this.shares = this.toSubscriptionShareModel(shares);
  }

  get isCompanySubscriber() {
    return this.shareholder?.isCompany || this.subscriber.organisationNumber;
  }

  get subscriberBirthdateOrOrgNo() {
    return this.shareholder
      ? this.shareholder.birthDateOrOrganisationNumber
      : this.subscriber.organisationNumber;
  }

  get isMultipleSigners() {
    return this.numberOfSigners > 1;
  }

  get numberOfSigners() {
    return this.signers.length;
  }

  get numberOfSignersWhoHasSigned() {
    return this.signersWhoHasSigned.length;
  }

  get signersWhoHasSigned() {
    return this.signers.filter((s) => s.isSigned);
  }

  get firstSigner() {
    return this.signersWhoHasSigned.length > 0
      ? this.signersWhoHasSigned.reduce((a, b) =>
          new Date(`${a.signingTime}`).getTime() < new Date(`${b.signingTime}`).getTime() ? a : b,
        )
      : undefined;
  }

  get isSubscriptionSigned() {
    return this.signers.some((s) => s.isSigned);
  }

  get isSubscriptionPeriod() {
    return this.subscriptionPeriod.hasStarted && !this.subscriptionPeriod.hasEnded;
  }

  get hasSubscriptionPeriodStarted() {
    return this.subscriptionPeriod.hasStarted;
  }

  get showSubscriptionPeriodEnded() {
    return this.subscriptionPeriod.hasEnded;
  }

  get showSubscriptionPeriodNotStarted() {
    return !this.subscriptionPeriod.hasEnded && !this.subscriptionPeriod.hasStarted;
  }

  get overSubscriptionLabel(): TMessage {
    return this.isOversubscriptionAvailable
      ? { key: "signing.subscription.form.overSubscription.true" }
      : { key: "signing.subscription.form.overSubscription.false" };
  }

  get totalSubscriptionPrice() {
    return this.shares
      .reduce(
        (sum, share) =>
          Big(sum).plus(Big(share.sharePrice).times(share.numberOfShares.toNumberOrZero())),
        Big(0),
      )
      .toNumber();
  }

  get isMultipleShareTypes() {
    return this.shares.length > 1;
  }

  get isDisabled() {
    return !this.hasSubscriptionPeriodStarted || this.isSubscriptionSigned;
  }

  get isValid(): boolean {
    return this.shares.every((d) => d.isValid);
  }

  validate(): boolean {
    this.shares.every((d) => d.validate());
    return this.isValid;
  }
}

export class SubscriptionShareModel {
  shareTypeId: number;
  shareTypeCode: ShareTypeCode;
  sharePrice: number;
  availableShares: number;
  numberOfShares: NumberFieldModel;
  isOversubscriptionAvailable?: boolean;

  constructor(data: SignSubscriptionFormShare, isOversubscriptionAvailable: boolean) {
    this.shareTypeId = data.sharetypeId;
    this.shareTypeCode = data.sharetypeCode;
    this.sharePrice = data.sharePrice;
    this.availableShares = data.availableShares;
    this.isOversubscriptionAvailable = isOversubscriptionAvailable;
    this.numberOfShares = new NumberFieldModel({
      value: data.numberOfShares,
      required: true,
      isPositiveOrZero: true,
      isInteger: true,
      validator: (value) => {
        const shares = value ?? 0;
        return shares < 0
          ? { key: "signing.subscription.form.shares.negative" }
          : !this.isOversubscriptionAvailable && this.availableShares < shares
            ? {
                key: "signing.subscription.form.shares.oversubscribe",
                params: { availableShares: this.availableShares },
              }
            : undefined;
      },
    });
  }
  get subscriptionPrice() {
    return Big(this.sharePrice).times(this.numberOfShares.toNumberOrZero()).toNumber();
  }

  get isValid(): boolean {
    return this.numberOfShares.isValid;
  }

  validate(): boolean {
    return this.numberOfShares.validate();
  }
}

export function mapToSignSubscriptionFormShares(
  model: SubscriptionModel | null,
): SignSubscriptionFormShare[] {
  if (model === null) return [];
  return model.shares.map((s) => {
    return {
      sharetypeId: s.shareTypeId,
      sharetypeCode: s.shareTypeCode,
      sharePrice: s.sharePrice,
      availableShares: s.availableShares,
      numberOfShares: s.numberOfShares.toNumberOrZero(),
    };
  });
}
